import type {
  WatermarkBlock,
  WatermarkTemplate,
} from '@frameio/graphql-client/generated';
import TEXT from './generated/TEXT/getFriendlyTemplateData';

TEXT.DEFINE({
  /** Label for viewer name in template descriptions */
  WatermarkDataPointUserName: 'Name',
  /** Label for viewer email in template descriptions */
  WatermarkDataPointEmail: 'Email',
  /** Label for viewer IP address in template descriptions */
  WatermarkDataPointIpAddress: 'IP address',
  /** Label for timestamp in template descriptions */
  WatermarkDataPointTimestamp: 'Date & Time',
  /** Label for admin-defined custom text in template descriptions */
  WatermarkDataPointCustomText: 'Custom Text',
  /** Label for share creator custom text in template descriptions */
  WatermarkDataPointUserInput: `Share creator's custom text`,
});

/**
 * getTemplateDescriptionFromBlocks - Returns a human-readable description of a watermark template based on template blocks.
 * @param blocks {WatermarkBlock[]} - The blocks that make up the watermark template.
 * @returns {string} A human-readable description of the watermark template.
 */
export const getTemplateDescriptionFromBlocks = (
  blocks: WatermarkBlock[]
): string => {
  if (!blocks || !blocks.length) {
    return '';
  }

  const uniqueTemplateBlocks = new Set<string>();

  const typeNameMap = {
    WatermarkDataPointUserName: TEXT.WatermarkDataPointUserName,
    WatermarkDataPointEmail: TEXT.WatermarkDataPointEmail,
    WatermarkDataPointIpAddress: TEXT.WatermarkDataPointIpAddress,
    WatermarkDataPointTimestamp: TEXT.WatermarkDataPointTimestamp,
    WatermarkDataPointCustomText: TEXT.WatermarkDataPointCustomText,
    WatermarkDataPointUserInput: TEXT.WatermarkDataPointUserInput,
  };

  blocks.forEach((block) => {
    block.dataPoints?.forEach(({ __typename }) => {
      const mappedValue = typeNameMap[__typename];
      if (mappedValue) {
        uniqueTemplateBlocks.add(mappedValue);
      }
    });
  });

  return Array.from(uniqueTemplateBlocks).join(', ');
};

export const getFriendlyTemplateData = (templates: WatermarkTemplate[]) => {
  if (!templates || !templates.length) {
    return [];
  }

  return templates?.map(({ name, id, blocks, deletedAt, inUse }) => ({
    name,
    id,
    templateDescription: getTemplateDescriptionFromBlocks(blocks),
    deletedAt,
    inUse,
  }));
};
